import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BaseAppLayout } from 'src/layouts';
import { useTranslation } from 'react-i18next';
import { Button, FoldersList } from '@business/workflows/ComponentUI';
import { SVGAssets } from '@business/assets/vectors';
import { EmptyScreen } from '@business/workflows/ComponentUI/Dashboard';
import { ConfirmationBox } from '@business/workflows/ComponentUI/ConfirmationBox';
import {
  CreateOptions,
  CreateOptionItem,
  EditProfilePictureModal
} from '@business/workflows/ComponentUI/Photos';
import { getAlertState, resetAlertState } from '@ports/common/AlertStateSlice';
import { AlertProps } from '@business/interfaces/alert';
import { Alert } from '@business/workflows/ComponentUI/Alert';
import {
  getUserNotificationData,
  setAdditionalPhotoMeta,
  setIsFromRecipesPage,
  setIsMediaUploadModalOpen,
  setModuleForUpload
} from '@ports/common/sessionStateSlice';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { routePaths } from '@utils/constants/routePaths';
import { resetFilterDetails } from '@ports/common/filtersStateSlice';
import { resetFolderDetails } from '@ports/common/folderStateSlice';
import { PhotosList } from '@business/workflows/ComponentUI';
import { useOutsideClickHandler } from '@utils/hooks';
import { useAssetCountQuery } from '@ports/internal-services/asset/assetSlice';
import { getUserData } from '@ports/common/userStateSlice';

const Photos = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const alertShowData = useSelector(getAlertState);
  const userNotificationData = useSelector(getUserNotificationData);
  const userData = useSelector(getUserData);
  // Set when landing back here after taking/uploading a profile picture via
  // the Camera route (see EditProfilePictureModal → TakeAPicture) — the
  // profile picture step is already done, so don't show it again.
  const cameFromProfilePictureFlow = Boolean(
    (location.state as { fromProfilePictureFlow?: boolean } | null)
      ?.fromProfilePictureFlow
  );
  const isPaymentSuccess =
    searchParams.get('status') === 'success' || cameFromProfilePictureFlow;

  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasClosedProfilePictureModal, setHasClosedProfilePictureModal] =
    useState<boolean>(cameFromProfilePictureFlow);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);

  const [isCreateNewFolder, setIsCreateNewFolder] = useState<boolean>(false);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [showCreateOptions, setShowCreateOptions] = useState<boolean>(false);
  const [isAsset, setIsAsset] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Derived fresh every render (not its own state) so every effect below
  // always agrees on the same value within a single render pass.
  const showProfilePictureModal =
    isPaymentSuccess && !isAsset && !hasClosedProfilePictureModal;

  const { data, refetch: refetchAssetCount } = useAssetCountQuery();

  const handleFolderDeleted = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const createButtonRef = useRef<HTMLDivElement>(null);
  useOutsideClickHandler(createButtonRef, () => {
    setShowCreateOptions(false);
  });

  useEffect(() => {
    if (alertShowData?.scope === 'photo' && alertShowData.alert) {
      setShowAlert(true);
      setAlertData(alertShowData.alert);
    }
  }, [alertShowData]);

  useEffect(() => {
    dispatch(setIsFromRecipesPage(false));
  }, []);

  useEffect(() => {
    if (data && data.data?.count > 0) {
      setIsAsset(true);
    } else {
      setIsAsset(false);
    }
  }, [data]);

  useEffect(() => {
    if (
      userNotificationData?.page === 'photos' &&
      userNotificationData?.notificationData?.assetId &&
      data?.data?.count === 0
    ) {
      refetchAssetCount();
    }
  }, [userNotificationData, data?.data?.count]);

  useEffect(() => {
    refetchAssetCount();
  }, []);

  const handleProfilePictureModalDone = () => {
    setHasClosedProfilePictureModal(true);
  };

  const handleCreateFolder = () => {
    setShowCreateOptions(false);
    setIsCreateNewFolder(true);
  };

  const handleUploadPhoto = () => {
    setShowCreateOptions(false);
    dispatch(setIsMediaUploadModalOpen(true));
    dispatch(setModuleForUpload('photos'));
  };

  const handleEmptyScreenUpload = () => {
    dispatch(setIsMediaUploadModalOpen(true));
    dispatch(setModuleForUpload('photos'));
  };

  useEffect(() => {
    dispatch(resetFilterDetails());
    dispatch(resetFolderDetails());
    dispatch(setAdditionalPhotoMeta({}));
    dispatch(setModuleForUpload(null));
  }, []);

  useEffect(() => {
    if (isAsset || showProfilePictureModal) {
      dispatch(setIsMediaUploadModalOpen(false));
      dispatch(setModuleForUpload(null));
    } else {
      dispatch(setIsMediaUploadModalOpen(true));
      dispatch(setModuleForUpload('photos'));
    }
  }, [isAsset, showProfilePictureModal]);

  const createOptionsItems: CreateOptionItem[] = [
    {
      id: 'create-folder',
      icon: <SVGAssets.Folder className="w-5 h-5" />,
      label: t('photosPage.createFolder'),
      onClick: handleCreateFolder
    },
    {
      id: 'upload-photo',
      icon: <SVGAssets.UploadIcon className="w-5 h-5 text-[#353535]" />,
      label: t('uploadPhoto'),
      onClick: handleUploadPhoto
    },
    {
      id: 'open-camera',
      icon: <SVGAssets.TakePicIcon className="w-5 h-5 text-[#353535]" />,
      label: t('photosPage.openCamera'),
      onClick: () => {
        navigate(routePaths.TakeAPhoto);
      }
    }
  ];

  return (
    <BaseAppLayout>
      {isAsset ? (
        <div className="flex flex-col h-full">
          {/* Add padding when selection bar is visible */}
          <div
            className={
              selectedPhotos.length > 0 || selectedFolders.length > 0
                ? 'pt-16'
                : ''
            }>
            <div className="items-center justify-between relative">
              <h1 className="text-2xl font-bold text-[#353535] pb-3">
                {t('photos')}
              </h1>
              {showAlert && (
                <div className="absolute right-0 top-0">
                  <Alert
                    onClose={() => {
                      dispatch(resetAlertState());
                      setShowAlert(false);
                    }}
                    {...alertData}
                    autoHide={true}
                  />
                </div>
              )}

              <div className="flex items-center justify-between relative">
                <div className="relative" ref={createButtonRef}>
                  <Button
                    btnStyle="primary"
                    type="button"
                    onClick={() => setShowCreateOptions(!showCreateOptions)}
                    className=" rounded-lg px-3 py-3 font-semibold text-sm flex text-center justify-center">
                    <SVGAssets.PlusRounded className="w-5 h-5 mr-2" />
                    {t('create')}
                  </Button>
                  {showCreateOptions && (
                    <CreateOptions options={createOptionsItems} />
                  )}
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 py-4 overflow-visible">
              <FoldersList
                setSelectedFolders={setSelectedFolders}
                selectedFolders={selectedFolders}
                selectedPhotos={selectedPhotos}
                setSelectedPhotos={setSelectedPhotos}
                isCreateNewFolder={isCreateNewFolder}
                setIsCreateNewFolder={setIsCreateNewFolder}
                onFolderDeleted={handleFolderDeleted}
              />

              <PhotosList
                selectedPhotos={selectedPhotos}
                setSelectedPhotos={setSelectedPhotos}
                selectedFolders={selectedFolders}
                setSelectedFolders={setSelectedFolders}
                isFilterEnabled={true}
                refreshTrigger={refreshTrigger}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <EmptyScreen
            onButtonClick={handleEmptyScreenUpload}
            icon={<SVGAssets.CameraIcon className="w-32 h-32 text-[#7F9D48]" />}
            title={t('dashboard.uploadPhotosTitle')}
            description={t('dashboard.uploadPhotosDescription')}
            btnName={t('dashboard.uploadPhotosButton')}
            feature="photos"
          />

          {isModalOpen && (
            <ConfirmationBox
              isOpen={isModalOpen}
              onCancel={() => setIsModalOpen(false)}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                setIsModalOpen(false);
                dispatch(setIsMediaUploadModalOpen(true));
                dispatch(setModuleForUpload('photos'));
              }}
              headerTitle={t('galleryAccess.headerTitle')}
              title={t('galleryAccess.title')}
              description={t('galleryAccess.description')}
              icon={
                <SVGAssets.UploadModalIcon className="w-20 h-20 text-[#7F9D48]" />
              }
              confirmText={t('galleryAccess.confirmText')}
              cancelText={t('galleryAccess.cancelText')}
            />
          )}

          {showProfilePictureModal && (
            <EditProfilePictureModal
              isOpen={showProfilePictureModal}
              userId={userData?.userId || ''}
              onClose={handleProfilePictureModalDone}
              onComplete={handleProfilePictureModalDone}
              onUploadSuccess={async () => {
                await refetchAssetCount();
              }}
            />
          )}
        </>
      )}
    </BaseAppLayout>
  );
};

export default Photos;
